import * as React from 'react'
import { SettingsPageWrapper } from '../../components/SettingsPageWrapper'

import { useParams } from 'react-router-dom'
import { ConfigurationType } from '@/app/constants/configuration/configurationType'
import { QUERY_KEY_SETTINGS_INTEGRATION } from '@/lib/api/constants'

import { IntegrationForm } from './IntegrationForm'
import { IntegrationSkeleton } from './IntegrationSkeleton'
import { useQueryIntegrationsSettings } from '@/lib/api/company/configuration/integration/useQueryIntegrationsSettings'
import { useMutationIntegrationsSettings } from '@/lib/api/company/configuration/integration/useMutationIntegrationsSettings'
import { IntegrationsConfigurationModel } from '@/lib/api/company/configuration/integration/types'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

// GPS data configuration page
export const IntegrationPageContent = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const { t } = useLocalize()
  const query = useQueryIntegrationsSettings({companyId})
  const mutation = useMutationIntegrationsSettings(companyId)
  const lastQueryData =
    React.useRef<IntegrationsConfigurationModel | null>(null)

  const [state, setState] =
    React.useState<IntegrationsConfigurationModel | null>(null)

  React.useEffect(() => {
    const data = query.data
    if (!data) return
    setState((prev) => ({
      ...prev,
      ...data,
    }))
    lastQueryData.current = data
  }, [query.data])

  const isDirty = React.useMemo(() => {
    return JSON.stringify(lastQueryData.current) !== JSON.stringify(state)
  }, [state, lastQueryData])

  const handleReset = () => {
    setState((prev) => ({
      ...prev,
      ...lastQueryData.current,
    }))
  }

  const handleSave = () => {
    mutation.mutate(state)
  }

  return (
    <SettingsPageWrapper
      isReady={!!state}
      isPending={query.isFetching || mutation.isPending}
      isError={query.isError}
      handleSave={handleSave}
      handleReset={handleReset}
      restore={{
        configurationType: ConfigurationType.Integration,
        tabLabelKey: 'configuration-menu.integration',
        queryKeys: [QUERY_KEY_SETTINGS_INTEGRATION],
        serverData: query.data,
        withDefaults: true,
      }}
      isDirty={isDirty}
      errorMsg={t('configuration-integration.load-error')}
      globalErrorMsg={t('configuration-integration.load-error-global')}
      diffBefore={lastQueryData.current as Record<string, unknown>}
      diffAfter={state as Record<string, unknown>}
    >
      {state ? (<IntegrationForm isSaving={mutation.isPending} state={state} setState={setState}/>) : (<IntegrationSkeleton />)}
    </SettingsPageWrapper>
  )
}
